// Prompt Management Admin Component
import { escapeHtml } from '../utils/html-utils.js';

class PromptManagement extends HTMLElement {
  constructor() {
    super();
    this.prompts = [];
    this.isLoading = true;
  }

  connectedCallback() {
    this.render();
    this.loadPrompts();
    this.attachEventListeners();
  }

  render() {
    this.innerHTML = `
      <div class="prompt-management">
        <div class="prompt-header">
          <h2>
            <washing-machine-icon size="medium" interactive="false"></washing-machine-icon>
            Writing Prompts Management
          </h2>
          <p class="prompt-description">
            Manage writing prompts that users can select when creating posts. 
            Approve user-submitted prompts to make them available in the global pool.
          </p>
          <button class="btn btn-primary add-prompt-btn" id="addPromptBtn">
            ➕ Add New Prompt
          </button>
        </div>

        <div class="prompt-stats">
          <div class="stat-card">
            <div class="stat-number" id="totalPrompts">-</div>
            <div class="stat-label">Total Prompts</div>
          </div>
          <div class="stat-card">
            <div class="stat-number" id="publicPrompts">-</div>
            <div class="stat-label">Public Prompts</div>
          </div>
          <div class="stat-card">
            <div class="stat-number" id="privatePrompts">-</div>
            <div class="stat-label">Private Prompts</div>
          </div>
        </div>

        <div class="prompt-content">
          <div class="loading-state" id="loadingState">
            <div class="spinner"></div>
            <div>Loading prompts...</div>
          </div>
          
          <div class="prompt-list" id="promptList" style="display: none;">
            <!-- Prompts will be loaded here -->
          </div>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Add new prompt button
    const addPromptBtn = this.querySelector('#addPromptBtn');
    addPromptBtn.addEventListener('click', () => {
      this.openAdminPromptModal();
    });

    // Event delegation for prompt actions
    this.addEventListener('click', (e) => {
      const actionBtn = e.target.closest('.action-btn');
      if (!actionBtn) return;

      const promptId = actionBtn.dataset.promptId;
      const action = actionBtn.dataset.action;

      if (action === 'promote') {
        this.updatePromptStatus(promptId, 'public');
      } else if (action === 'demote') {
        this.updatePromptStatus(promptId, 'private');
      }
    });
  }

  async loadPrompts() {
    this.isLoading = true;
    const loadingState = this.querySelector('#loadingState');
    const promptList = this.querySelector('#promptList');

    loadingState.style.display = 'block';
    promptList.style.display = 'none';

    try {
      const response = await window.API.request('/prompts/admin/all');
      this.prompts = response.prompts || [];
      this.renderPrompts();
      this.updateStats();
    } catch (error) {
      window.logger.error('Failed to load prompts:', error);
      this.renderError();
    } finally {
      this.isLoading = false;
      loadingState.style.display = 'none';
      promptList.style.display = 'block';
    }
  }

  renderPrompts() {
    const promptList = this.querySelector('#promptList');

    if (this.prompts.length === 0) {
      promptList.innerHTML = `
        <div style="text-align: center; padding: 3rem; color: var(--text-secondary);">
          <div style="font-size: 3rem; margin-bottom: 1rem;">🧺</div>
          <h3>No prompts found</h3>
          <p>Users haven't submitted any writing prompts yet.</p>
        </div>
      `;
      return;
    }

    promptList.innerHTML = this.prompts.map(prompt => `
      <div class="prompt-item">
        <div class="prompt-item-header">
          <div class="prompt-item-info">
            <h3 class="prompt-title">${escapeHtml(prompt.title)}</h3>
            <div class="prompt-meta">
              <span>Created by: <strong>${escapeHtml(prompt.createdBy)}</strong></span>
              <span>•</span>
              <span>${new Date(prompt.createdAt).toLocaleDateString()}</span>
            </div>
            ${prompt.body ? `
              <div class="prompt-body">${escapeHtml(prompt.body)}</div>
            ` : ''}
          </div>
          <div class="prompt-actions">
            <div class="status-badge ${prompt.status}">
              ${prompt.status}
            </div>
            ${prompt.status === 'private' ? `
              <button class="action-btn promote" data-prompt-id="${prompt.id}" data-action="promote">
                ✅ Approve
              </button>
            ` : `
              <button class="action-btn demote" data-prompt-id="${prompt.id}" data-action="demote">
                ❌ Unapprove
              </button>
            `}
          </div>
        </div>
      </div>
    `).join('');
  }

  updateStats() {
    const totalPrompts = this.prompts.length;
    const publicPrompts = this.prompts.filter(p => p.status === 'public').length;
    const privatePrompts = this.prompts.filter(p => p.status === 'private').length;

    this.querySelector('#totalPrompts').textContent = totalPrompts;
    this.querySelector('#publicPrompts').textContent = publicPrompts;
    this.querySelector('#privatePrompts').textContent = privatePrompts;
  }

  async updatePromptStatus(promptId, newStatus) {
    try {
      await window.API.request(`/prompts/admin/${promptId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });

      // Update local data (convert promptId to number for comparison)
      const prompt = this.prompts.find(p => p.id === parseInt(promptId));
      if (prompt) {
        prompt.status = newStatus;
      }

      // Re-render
      this.renderPrompts();
      this.updateStats();

      // Show success message
      this.showMessage(`Prompt ${newStatus === 'public' ? 'approved' : 'unapproved'} successfully!`, 'success');
    } catch (error) {
      window.logger.error('Failed to update prompt status:', error);
      this.showMessage('Failed to update prompt status. Please try again.', 'error');
    }
  }

  renderError() {
    const promptList = this.querySelector('#promptList');
    promptList.innerHTML = `
      <div style="text-align: center; padding: 3rem; color: var(--text-secondary);">
        <div style="font-size: 3rem; margin-bottom: 1rem;">😞</div>
        <h3>Failed to load prompts</h3>
        <p>There was an error loading the writing prompts. Please refresh the page to try again.</p>
      </div>
    `;
  }

  showMessage(message, type = 'info') {
    // Create a temporary message element
    const messageEl = document.createElement('div');
    messageEl.style.cssText = `
      position: fixed;
      top: 2rem;
      right: 2rem;
      background: ${type === 'success' ? '#10b981' : '#ef4444'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      font-weight: 500;
      z-index: 10000;
      animation: slideIn 0.3s ease-out;
    `;
    messageEl.textContent = message;

    // Add animation styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(messageEl);

    // Remove after 3 seconds
    setTimeout(() => {
      messageEl.remove();
      style.remove();
    }, 3000);
  }

  openAdminPromptModal() {
    // Create modal dynamically
    const modal = document.createElement('div');
    modal.className = 'admin-prompt-modal';
    modal.innerHTML = `
      <div class="modal-backdrop">
        <div class="modal-content">
          <div class="modal-header">
            <h3>➕ Create New Writing Prompt</h3>
            <button class="close-btn" id="closeAdminModal">&times;</button>
          </div>
          <form class="admin-prompt-form" id="adminPromptForm">
            <div class="form-group">
              <label for="adminPromptTitle">Prompt Title *</label>
              <input 
                type="text" 
                id="adminPromptTitle" 
                name="title" 
                placeholder="e.g., What's your favorite childhood memory?"
                maxlength="255"
                required
              />
            </div>
            <div class="form-group">
              <label for="adminPromptBody">
                Optional Instructions
                <span class="help-text">(Additional context or template)</span>
              </label>
              <textarea 
                id="adminPromptBody" 
                name="body" 
                placeholder="e.g., Describe the memory in detail and explain why it's special to you..."
                rows="3"
                maxlength="1000"
              ></textarea>
            </div>
            <div class="form-group">
              <label for="adminPromptStatus">Initial Status</label>
              <select id="adminPromptStatus" name="status">
                <option value="private">Private (review later)</option>
                <option value="public" selected>Public (available immediately)</option>
              </select>
            </div>
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" id="cancelAdminPrompt">Cancel</button>
              <button type="submit" class="btn btn-primary" id="submitAdminPrompt">
                <span class="btn-text">Create Prompt</span>
                <span class="btn-loading" style="display: none;">⏳ Creating...</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Add event listeners
    modal.querySelector('#closeAdminModal').addEventListener('click', () => {
      this.closeAdminModal(modal);
    });

    modal.querySelector('#cancelAdminPrompt').addEventListener('click', () => {
      this.closeAdminModal(modal);
    });

    modal.querySelector('.modal-backdrop').addEventListener('click', (e) => {
      if (e.target.classList.contains('modal-backdrop')) {
        this.closeAdminModal(modal);
      }
    });

    modal.querySelector('#adminPromptForm').addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleAdminPromptSubmission(modal);
    });

    // Focus on title input
    setTimeout(() => {
      modal.querySelector('#adminPromptTitle').focus();
    }, 100);
  }

  closeAdminModal(modal) {
    modal.remove();
  }

  async handleAdminPromptSubmission(modal) {
    const titleInput = modal.querySelector('#adminPromptTitle');
    const bodyInput = modal.querySelector('#adminPromptBody');
    const statusSelect = modal.querySelector('#adminPromptStatus');
    const submitBtn = modal.querySelector('#submitAdminPrompt');
    const btnText = submitBtn.querySelector('.btn-text');
    const btnLoading = submitBtn.querySelector('.btn-loading');

    const title = titleInput.value.trim();
    const body = bodyInput.value.trim();
    const status = statusSelect.value;

    if (!title) {
      alert('Please enter a prompt title');
      titleInput.focus();
      return;
    }

    // Set loading state
    submitBtn.disabled = true;
    btnText.style.display = 'none';
    btnLoading.style.display = 'inline';

    try {
      // Create the prompt
      const response = await window.API.prompts.create({
        title: title,
        body: body || null
      });

      // If admin wants it public immediately, update status
      if (status === 'public') {
        await window.API.prompts.updateStatus(response.prompt.id, 'public');
      }

      // Show success message
      this.showMessage(`Prompt created and set to ${status}!`, 'success');

      // Close modal and reload
      this.closeAdminModal(modal);
      await this.loadPrompts();

    } catch (error) {
      window.logger.error('Failed to create admin prompt:', error);
      alert('Failed to create prompt. Please try again.');

      // Reset button state
      submitBtn.disabled = false;
      btnText.style.display = 'inline';
      btnLoading.style.display = 'none';
    }
  }

}

// Register the custom element
customElements.define('prompt-management', PromptManagement);
